import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * WheelTimePickerComponent - Stepper-style time picker (HH : MM)
 *
 * Features:
 * - Two columns with up/down buttons: hours (00-23) and minutes (00-59)
 * - Step of 1 by default; configurable minute step (e.g. 5, 15)
 * - Wraps around at edges (23 -> 00, 00 -> 23 for hours; 59 -> 00, etc.)
 * - Manual numeric input with strict validation (range + minute step)
 * - Hold-to-repeat on the stepper buttons with progressive acceleration
 * - ControlValueAccessor implementation for reactive forms integration
 * - Format: HH : MM with centered separator
 */
@Component({
  selector: 'usb-wheel-time-picker',
  templateUrl: './wheel-time-picker.component.html',
  styleUrls: ['./wheel-time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WheelTimePickerComponent),
      multi: true
    }
  ]
})
export class WheelTimePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  @Input() initialValue: string = '14:30';
  @Input() minuteStep: number = 1;
  @Input() disabled: boolean = false;
  // Optional floor. When set, the picker clamps emitted values and its
  // initial display so the user cannot pick or see a time below this floor.
  // Format: "HH:MM". Strings are compared lexicographically (safe because
  // both hour and minute are zero-padded to 2 digits).
  @Input() minTime: string | null = null;
  // Emitted on Enter after the value is committed, so hosts can close any
  // wrapping dropdown.
  @Output() closed = new EventEmitter<void>();

  // Reference to the hour input for autofocus on mount. Needed because the
  // host's readonly trigger input never passes focus here.
  @ViewChild('hourInput') hourInputRef?: ElementRef<HTMLInputElement>;

  selectedHour: string = '00';
  selectedMinute: string = '00';

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  // Hold-to-repeat state
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private holdInterval: ReturnType<typeof setInterval> | null = null;
  private holdStartedAt: number = 0;
  private holdAction: (() => void) | null = null;

  // Hold-to-repeat tuning
  private static readonly HOLD_INITIAL_DELAY_MS = 400;
  private static readonly HOLD_BASE_INTERVAL_MS = 80;
  private static readonly HOLD_FAST_INTERVAL_MS = 30;
  private static readonly HOLD_ACCEL_AFTER_MS = 2000;

  ngOnInit(): void {
    this.parseInitialValue();
  }

  ngAfterViewInit(): void {
    document.addEventListener('keydown', this.nativeEnterListener, true);
  }

  ngOnDestroy(): void {
    this.clearHold();
    document.removeEventListener('keydown', this.nativeEnterListener, true);
  }

  // ========== STEPPER HANDLERS ==========

  incrementHour(): void {
    this.updateHour(this.toNumber(this.selectedHour) + 1);
  }

  decrementHour(): void {
    this.updateHour(this.toNumber(this.selectedHour) - 1);
  }

  incrementMinute(): void {
    this.updateMinute(this.toNumber(this.selectedMinute) + this.minuteStep);
  }

  decrementMinute(): void {
    this.updateMinute(this.toNumber(this.selectedMinute) - this.minuteStep);
  }

  // ========== HOLD-TO-REPEAT ==========

  // Capture-phase Enter listener. Required because @HostListener runs in
  // the bubble phase, AFTER the parent <form>'s implicit submit on Enter.
  // With capture:true we intercept before the form sees the event.
  private nativeEnterListener = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return;
    }
    if (this.disabled) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const isOurs = target.classList?.contains('stepper-value') &&
      (target.getAttribute('aria-label') === 'Hora' ||
       target.getAttribute('aria-label') === 'Minutos');
    if (!isOurs) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (target.getAttribute('aria-label') === 'Hora') {
      this.commitHour(this.selectedHour);
    } else {
      this.commitMinute(this.selectedMinute);
    }
    this.closed.emit();
  };

  startHold(action: () => void, event?: Event): void {
    if (this.disabled) {
      return;
    }
    // Prevent text selection / focus stealing on long press
    event?.preventDefault();
    this.clearHold();
    this.holdAction = action;
    this.holdStartedAt = Date.now();
    this.holdTimer = setTimeout(() => {
      this.tickHold();
      this.holdInterval = setInterval(() => this.tickHold(), this.currentHoldInterval());
    }, WheelTimePickerComponent.HOLD_INITIAL_DELAY_MS);
  }

  stopHold(): void {
    this.clearHold();
  }

  private tickHold(): void {
    if (!this.holdAction) {
      return;
    }
    this.holdAction();
    // Re-evaluate interval so acceleration kicks in mid-press
    if (this.holdInterval) {
      clearInterval(this.holdInterval);
      this.holdInterval = setInterval(() => this.tickHold(), this.currentHoldInterval());
    }
  }

  private currentHoldInterval(): number {
    const elapsed = Date.now() - this.holdStartedAt;
    return elapsed >= WheelTimePickerComponent.HOLD_ACCEL_AFTER_MS
      ? WheelTimePickerComponent.HOLD_FAST_INTERVAL_MS
      : WheelTimePickerComponent.HOLD_BASE_INTERVAL_MS;
  }

  private clearHold(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
    if (this.holdInterval !== null) {
      clearInterval(this.holdInterval);
      this.holdInterval = null;
    }
    this.holdAction = null;
    this.holdStartedAt = 0;
  }

  // Stop the repeat if the user releases outside the button (mouse drag away, blur, etc.)
  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDocumentRelease(): void {
    if (this.holdTimer !== null || this.holdInterval !== null) {
      this.clearHold();
    }
  }

  // ========== WHEEL SCROLL ==========

  // Accumulate fractional wheel deltas so a fast flick advances by multiple
  // steps (browser line/page modes vary wildly across OS/trackpads).
  private wheelAccumulator: { hour: number; minute: number } = { hour: 0, minute: 0 };

  // Route wheel events to the right field based on which input is under
  // the cursor. Registered as a non-passive host listener so we can call
  // preventDefault and stop the page from scrolling while the user rolls
  // over the picker.
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (this.disabled) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    // Only react when the wheel is over one of the two numeric inputs.
    const isHour = target.classList?.contains('stepper-value') &&
      target.getAttribute('aria-label') === 'Hora';
    const isMinute = target.classList?.contains('stepper-value') &&
      target.getAttribute('aria-label') === 'Minutos';
    if (isHour) {
      this.applyWheel(event, 'hour');
    } else if (isMinute) {
      this.applyWheel(event, 'minute');
    }
  }

  private applyWheel(event: WheelEvent, field: 'hour' | 'minute'): void {
    event.preventDefault();
    // Normalize wheel deltas so one notch ≈ one step. Pixels (deltaMode 0)
    // report ~100 per notch, lines (deltaMode 1) report ~1-3. Without this
    // a single pixel-mode event would consume dozens of steps.
    const unit = event.deltaMode === 0 ? 80 : event.deltaMode === 1 ? 16 : 80;
    this.wheelAccumulator[field] += (event.deltaY / unit);
    // 0.5 threshold: a half-notch advances one step, so continuous trackpad
    // scroll feels smooth without a single notch skipping ahead.
    if (this.wheelAccumulator[field] >= 0.5) {
      const steps = Math.floor(this.wheelAccumulator[field]);
      this.wheelAccumulator[field] -= steps;
      for (let i = 0; i < steps; i++) {
        field === 'hour' ? this.incrementHour() : this.incrementMinute();
      }
    } else if (this.wheelAccumulator[field] <= -0.5) {
      const steps = Math.floor(-this.wheelAccumulator[field]);
      this.wheelAccumulator[field] += steps;
      for (let i = 0; i < steps; i++) {
        field === 'hour' ? this.decrementHour() : this.decrementMinute();
      }
    }
  }

  // ========== MANUAL INPUT ==========

  onHourInput(value: string): void {
    const sanitized = this.sanitizeDigits(value, 2);
    this.selectedHour = sanitized;
    // Emit the in-progress value so the parent form reflects what the user
    // typed. The form will be normalized (clamped/snapped) on blur/Enter.
    // Not emitting here was leaving the form stuck on the previous value
    // until the input lost focus, which never happens reliably when the
    // picker is hosted inside a dropdown that closes on outside click.
    this.emitValue();
  }

  onMinuteInput(value: string): void {
    const sanitized = this.sanitizeDigits(value, 2);
    this.selectedMinute = sanitized;
    this.emitValue();
  }

  onHourBlur(): void {
    this.commitHour(this.selectedHour);
  }

  onMinuteBlur(): void {
    this.commitMinute(this.selectedMinute);
  }

  onHourKeydown(event: KeyboardEvent): void {
    this.handleEditKey(event, () => this.commitHour(this.selectedHour));
  }

  onMinuteKeydown(event: KeyboardEvent): void {
    this.handleEditKey(event, () => this.commitMinute(this.selectedMinute));
  }

  private handleEditKey(event: KeyboardEvent, commit: () => void): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      (event.target as HTMLInputElement).blur();
      this.closed.emit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Restore from current emitted value (re-parse selectedHour/Minute from parent)
      const current = this.currentValue();
      const [h, m] = current.split(':');
      this.selectedHour = h;
      this.selectedMinute = m;
      (event.target as HTMLInputElement).blur();
    }
  }

  private commitHour(raw: string): void {
    const num = this.toNumber(raw);
    if (Number.isNaN(num)) {
      this.selectedHour = '00';
    } else {
      const clamped = Math.min(23, Math.max(0, num));
      this.selectedHour = clamped.toString().padStart(2, '0');
    }
    if (this.selectedHour !== raw) {
      this.emitValue();
    } else {
      this.onTouched();
    }
  }

  private commitMinute(raw: string): void {
    const num = this.toNumber(raw);
    if (Number.isNaN(num)) {
      this.selectedMinute = (0).toString().padStart(2, '0');
    } else {
      const clamped = Math.min(59, Math.max(0, num));
      const snapped = Math.round(clamped / this.minuteStep) * this.minuteStep;
      const normalized = snapped % 60;
      this.selectedMinute = normalized.toString().padStart(2, '0');
    }
    if (this.selectedMinute !== raw) {
      this.emitValue();
    } else {
      this.onTouched();
    }
  }

  private sanitizeDigits(value: string, maxLength: number): string {
    const digits = (value || '').replace(/\D/g, '').slice(0, maxLength);
    return digits;
  }

  // ========== HELPERS ==========

  private updateHour(value: number): void {
    const wrapped = ((value % 24) + 24) % 24;
    const next = wrapped.toString().padStart(2, '0');
    if (next !== this.selectedHour) {
      this.selectedHour = next;
      this.emitValue();
    }
  }

  private updateMinute(value: number): void {
    const wrapped = ((value % 60) + 60) % 60;
    const snapped = Math.round(wrapped / this.minuteStep) * this.minuteStep;
    const normalized = snapped % 60;
    const next = normalized.toString().padStart(2, '0');
    if (next !== this.selectedMinute) {
      this.selectedMinute = next;
      this.emitValue();
    }
  }

  private toNumber(value: string): number {
    return parseInt(value, 10);
  }

  private currentValue(): string {
    return `${this.selectedHour}:${this.selectedMinute}`;
  }

  private parseInitialValue(): void {
    if (this.initialValue && this.initialValue.includes(':')) {
      const raw = this.applyFloor(this.initialValue);
      const [hour, minute] = raw.split(':');
      this.selectedHour = hour.padStart(2, '0');
      const minNum = parseInt(minute, 10);
      const closestMinute = Math.round(minNum / this.minuteStep) * this.minuteStep;
      this.selectedMinute = (closestMinute % 60).toString().padStart(2, '0');
    }
  }

  private emitValue(): void {
    const raw = `${this.selectedHour}:${this.selectedMinute}`;
    const clamped = this.applyFloor(raw);
    if (clamped !== raw) {
      // Sync internal state so the wheel visually reflects the floor after
      // the user scrolls/picks a value below it.
      const [h, m] = clamped.split(':');
      this.selectedHour = h;
      this.selectedMinute = m;
    }
    this.onChange(clamped);
    this.onTouched();
  }

  /** Clamp a HH:MM value to the floor when the floor is set and value < floor. */
  private applyFloor(value: string): string {
    if (this.minTime && value < this.minTime) {
      return this.minTime;
    }
    return value;
  }

  // ========== ControlValueAccessor ==========

  writeValue(value: string): void {
    if (value && value.includes(':')) {
      const raw = this.applyFloor(value);
      const [hour, minute] = raw.split(':');
      this.selectedHour = hour.padStart(2, '0');
      const minNum = parseInt(minute, 10);
      const closestMinute = Math.round(minNum / this.minuteStep) * this.minuteStep;
      this.selectedMinute = (closestMinute % 60).toString().padStart(2, '0');
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
